"use client";

import { useEffect, useState } from "react";
import { useUser } from "@clerk/nextjs";
import { getAllProblemHistories } from "@/lib/server-actions/problem/history";
import type { ProblemHistory } from "@/lib/types";

/**
 * 대시보드에서 풀이 이력을 한 번만 fetch해 정답률 차트·최근 오답노트 위젯이 공유한다(JCG-2073).
 * `getAllProblemHistories`는 전량 fetch라 무거우므로 위젯마다 따로 부르지 않고 페이지에서 1회만 호출.
 *
 * JCG-3430: `noteOnlyHistories`는 활성 회차 시도 기록 없이 오답노트만 있는 문제(채점 없이 정답보기
 * 등). 정답률·푼 문제 수를 왜곡하지 않도록 `histories`와 분리해 두고, 최근 오답노트 위젯만 합쳐 쓴다.
 */
export function useAllProblemHistories() {
  const { isSignedIn, isLoaded } = useUser();
  const [histories, setHistories] = useState<ProblemHistory[]>([]);
  const [noteOnlyHistories, setNoteOnlyHistories] = useState<ProblemHistory[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoaded) return; // Clerk 로드 전엔 대기
    let alive = true;
    // 비로그인 시 호출하면 서버에서 userId 없음 → Sentry "Missing userId" warning + 낭비(코드리뷰 #2).
    // async IIFE 안에서 처리해 set-state-in-effect 룰 준수.
    (async () => {
      if (!isSignedIn) {
        if (alive) setLoading(false);
        return;
      }
      try {
        const res = await getAllProblemHistories();
        if (alive && res?.success) {
          setHistories(res.histories);
          setNoteOnlyHistories(res.noteOnlyHistories ?? []);
        }
      } catch (e) {
        console.error("대시보드 풀이 이력 로드 오류:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isLoaded, isSignedIn]);

  return { histories, noteOnlyHistories, loading };
}
